import { useState } from 'react';
import { Stack, Title, Card, Text, Group, Badge, List, Button, Skeleton, Alert, Divider } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSparkles, IconAlertCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useBillingPlan } from '../../hooks/useBilling';
import { useUpgradeTrial } from '../../hooks/useBillingMutations';
import { AccountUtils } from '../../AccountUtils';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PaymentMethodForm } from './PaymentMethodForm';
import type { PaymentMethodFormValues } from '../../schemas/billingSchemas';
import type { IBillingAccountViewData } from '@/lib/types/Billing';

export function TrialUpgradePage() {
  const navigate = useNavigate();
  const { billingPlan, isLoading } = useBillingPlan();
  const upgradeMutation = useUpgradeTrial();
  const { customerInfo, authData } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);

  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upgrade Your Plan</Title>
        <Skeleton height={200} radius="md" />
        <Skeleton height={300} radius="md" />
      </Stack>
    );
  }

  if (!billingPlan) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upgrade Your Plan</Title>
        <Text c="dimmed">Unable to load plan information.</Text>
      </Stack>
    );
  }

  const currentPlan = billingPlan.billingPlan;
  const upgradePlan = billingPlan.recommendedUpgradePlan;

  // Build empty billing data for a new payment method form
  const emptyPaymentMethod: IBillingAccountViewData = {
    billingAccountId: '',
    billingAccountType: 'card',
    fullName: '',
    orgName: null,
    address1: '',
    city: '',
    region: '',
    postalCode: '',
    countryIso2: 'US',
    cardDetails: null,
    created: 0,
    updated: 0,
  };

  async function handleSubmit(values: PaymentMethodFormValues) {
    if (!parentCId || !upgradePlan) return;
    setAuthError(null);

    const billingData: IBillingAccountViewData = {
      ...emptyPaymentMethod,
      fullName: values.fullName,
      orgName: values.orgName || null,
      address1: values.address1,
      city: values.city,
      region: values.region,
      postalCode: values.postalCode,
      countryIso2: values.countryIso2,
      cardDetails: {
        cardNum: values.cardNum,
        cardLastFour: values.cardNum.slice(-4),
        cardType: 'visa',
        cardMonth: values.cardMonth,
        cardYear: values.cardYear,
        cvv: values.cvv,
      },
    };

    try {
      const result = await upgradeMutation.mutateAsync({
        cId: parentCId,
        billing: billingData,
        planId: upgradePlan.id,
      });

      if (result.authResult && result.authResult.responseCodeName !== 'approved') {
        setAuthError(result.authResult.reason || 'Payment was declined. Please check your card details.');
        return;
      }

      notifications.show({
        title: 'Upgrade Successful',
        message: `You have been upgraded to the ${upgradePlan.publicLabel || upgradePlan.name} plan.`,
        color: 'green',
      });
      navigate({ to: '/account/usage' });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to upgrade plan.',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Upgrade Your Plan</Title>

      <Group grow align="stretch">
        <PlanCard
          label="Current Plan"
          plan={currentPlan}
          variant="current"
        />
        {upgradePlan && (
          <PlanCard
            label="Recommended Plan"
            plan={upgradePlan}
            variant="recommended"
          />
        )}
      </Group>

      {upgradePlan && (
        <>
          <Divider />
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack gap="md">
              <Group gap="sm">
                <IconSparkles size={20} />
                <Text fw={600} size="lg">Enter Payment Details</Text>
              </Group>
              <Text size="sm" c="dimmed">
                You will be charged ${upgradePlan.pricePerMonth.toFixed(2)}/month for the{' '}
                {upgradePlan.publicLabel || upgradePlan.name} plan.
              </Text>
              <PaymentMethodForm
                paymentMethod={emptyPaymentMethod}
                onSubmit={handleSubmit}
                onCancel={() => navigate({ to: '/account/usage' })}
                isPending={upgradeMutation.isPending}
                authError={authError}
              />
            </Stack>
          </Card>
        </>
      )}

      {!upgradePlan && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          No upgrade plan is available at this time. Please contact support.
        </Alert>
      )}
    </Stack>
  );
}

function PlanCard({
  label,
  plan,
  variant,
}: {
  label: string;
  plan: { name: string; publicLabel: string; pricePerMonth: number; features: string[]; limitStorageGB: number; limitTransferGB: number };
  variant: 'current' | 'recommended';
}) {
  const isRecommended = variant === 'recommended';

  return (
    <Card
      shadow={isRecommended ? 'md' : 'sm'}
      padding="lg"
      radius="md"
      withBorder
      style={isRecommended ? { borderColor: 'var(--mantine-color-blue-4)', borderWidth: 2 } : undefined}
    >
      <Stack gap="sm">
        <Group justify="space-between">
          <Text size="sm" c="dimmed" tt="uppercase" fw={500}>{label}</Text>
          {isRecommended && <Badge color="blue">Recommended</Badge>}
        </Group>
        <Text fw={700} size="xl">{plan.publicLabel || plan.name}</Text>
        <Text fw={600} size="lg" c={isRecommended ? 'blue' : undefined}>
          ${plan.pricePerMonth.toFixed(2)}<Text span size="sm" c="dimmed">/month</Text>
        </Text>
        <Divider />
        <Stack gap={4}>
          <Text size="sm">Storage: {plan.limitStorageGB} GB</Text>
          <Text size="sm">Transfer: {plan.limitTransferGB} GB</Text>
        </Stack>
        {plan.features.length > 0 && (
          <List size="sm" spacing={4}>
            {plan.features.map((f, i) => (
              <List.Item key={i}>{f}</List.Item>
            ))}
          </List>
        )}
      </Stack>
    </Card>
  );
}
